import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from "next-seo";
import { Message } from "semantic-ui-react";
import Link from 'next/link';
import { getCta } from 'utils/common';

const AirBnbArticle = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/airbnb-foodies`;

  return (
    <MainLayout>
      <NextSeo
        title='Ultimate Guide to 8 AirBnB Experiences for Foodies | DAYMADE'
        description='Foodie alert! Time to let your tastebuds explore the best of local culture. Learn to make ramen, the best sandwiches, Japanese tea and more on these 8 Airbnb experiences for foodies!'
        openGraph={{
          title: 'Ultimate Guide to 8 AirBnB Experiences for Foodies | DAYMADE',
          description: 'Foodie alert! Time to let your tastebuds explore the best of local culture. Learn to make ramen, the best sandwiches, Japanese tea and more on these 8 Airbnb experiences for foodies!',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/ultimate-guide-to-8-airbnb-experiences-for-foodies',
          images: [{
            url: `${IMAGE_DIRECTORY}/hero.jpg`,
            alt: 'woman solo-eating asian food with chopsticks'
          }]
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'instagram,instagrammable,airbnb,competition,foodies,travel,destinations,holiday,prizes,getaway,peru,philippines,california,japan,croatia,new york,kenya,bali,meal,food,story'
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText='woman solo-eating asian food with chopsticks'
        heroImageYPosition='0%'
      >
        <>
          <h1 style={{ fontSize: '1.6em' }}>
             8 AirBnb Experiences for <strong>Foodies</strong>
          </h1>

          <p className='subheading'>
            TripHunter’s Ultimate Guide
          </p>

          <div className='separator' style={{ width: '80%', marginTop: 10 }} />

          <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '2em' }}>
            Have you ever travelled somewhere solely for the food? Then you have officially earned your membership in the foodie club – welcome! If you’re a food lover like we are at DAYMADE, finding the best local experiences is what travelling is all about. Food and travelling go together like pumpkin and spice, pasta and wine, sushi and sake, well, you get the idea. Read on for our 2019 shortlist of deliciously authentic AirBnb experiences across the world that you can really bite into!
          </p>

          <p>
            <span className='bigFirstLetter'>T</span>here’s really no better way to experience a new destination than by noshing your way through the best eateries in town. And by best, we mean the most authentic and delicious local dishes. A true foodie wants to experience the most original and tastiest dishes when away from home. That’s why linking up with local AirBnb experts ready to share their passion for native cuisine is the perfect way to satisfy your wanderlust-appetite. If you follow your belly when it’s holiday planning time, then read on for some seriously scrumptious AirBnb experiences around the globe that are sure to dazzle your taste buds.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt='brush with green sauce and bowl of powdered wasabi'
            className='singleBlogImage'
          />

          <h4>
            Peru
          </h4>

          <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1em' }}>
            Ceviche: Fish, limes and tasty times!
          </p>

          <p>
            You don’t have to hike Machu Picchu to get the real Peruvian experience – the country’s true treasure lies in its rich culinary heritage. Considered to be the national dish, Ceviche comes in second to none in the Peruvian kitchen. Consisting of raw fish, lime juice and chilli or hot peppers, this national gem can be found on nearly every corner. But the real treat is having a home-made ceviche experience – made by you! There are plenty of AirBnb hosts in Peru offering the chance to cook and eat real Andean Ceviche in a Peruvian home – and perhaps you’ll even end up with a secret local recipe to take back home with you.
          </p>

          <p>
            <a href='https://www.airbnb.com/experiences/339701?source=p2' style={{ fontWeight: 'bold', margin: 0 }}>
              AirBnB Experience →
            </a>
          </p>

          <h4>
            Philippines
          </h4>

          <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1em' }}>
            Market stalls deserving of a Michelin star
          </p>

          <p>
            Every traveller knows that real life happens on the street. That’s why a food crawl through the markets of Manila is the perfect way to explore the city’s lively local vibe. The best part about an AirBnb walking tour through the markets is that your local guide will navigate, so you can be sure to discover the tastiest dives in town (and skip the tourist traps). The food stands in Manila pack so much flavour that a market tour is sure to change your original "Skip Manila, go to the Beaches!" plan. Who knows, you may even stay a few extra days – just to eat some more!
          </p>

          <p>
            <a href='https://www.airbnb.com/experiences/1174885?source=p2' style={{ fontWeight: 'bold', margin: 0 }}>
              AirBnB Experience →
            </a>
          </p>

          <h4>
            California
          </h4>

          <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1em' }}>
            Cook Ramen in San Francisco
          </p>

          <p>
            Up your Japanese street food game by learning to cook Ramen in the Bay Area. If you love Ramen and find yourself in San Francisco, this interactive dining adventure is a must! As a completely hands-on experience, you will learn to make your own Ramen noodles and soup from scratch. You can even skip the expensive ticket to Japan – after only one class you’ll be able to recreate this popular dish at home for your friends and family to enjoy!
          </p>

          <p>
            <a href='https://www.airbnb.com/experiences/115556?source=p2' style={{ fontWeight: 'bold', margin: 0 }}>
              AirBnB Experience →
            </a>
          </p>

          <div className='doubleImageContainer'>
            <img
              src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
              alt='bowl of various vegetables including raw red onions'
              className='doubleBlogImage'
            />

            <img
              src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
              alt='prawn ramen bowl with egg, pees, sesame seeds, and blue chopsticks'
              className='doubleBlogImage'
            />
          </div>

          <h4>
            Japan
          </h4>

          <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1em' }}>
            The Way of Tea
          </p>

          <p>
            Ditch the coffee, it’s tea time in Kyoto! AirBnb offers plenty of authentic experiences to learn the “Way of Tea”, one of Japan’s most renowned traditional practices. Share in an authentic tea ceremony at a local tea house and learn how to make matcha from licensed teachers. The Way of Tea is not just about making or drinking tea. Tea ceremonies are connected with other aspects of Japanese culture such as Ikebana (traditional flower arrangement) and Shodō (traditional calligraphy). You can’t leave Kyoto without visiting a tea ceremony hosted by a local Airbnb tea expert!
          </p>

          <p>
            <a href='https://www.airbnb.com/experiences/275064?source=p2' style={{ fontWeight: 'bold', margin: 0 }}>
              AirBnB Experience →
            </a>
          </p>

          <h4>
            Croatia
          </h4>

          <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1em' }}>
            Calling all Game of Thrones fans!
          </p>

          <p>
            Venture to the heart of Westeros in a walking tour through the city of Split. The impressively preserved monuments will bring Game of Thrones to life as you walk the streets of King’s Landing and visit the many spots where all your favourite (or not so favourite) GoT characters plotted and fought for the Seven Kingdoms. Top off your tour with a feast fit for Tyrion Lannister – of course with some local wine.
          </p>

          <p>
            <a href='https://www.airbnb.com/experiences/803818?source=p2' style={{ fontWeight: 'bold', margin: 0 }}>
              AirBnB Experience →
            </a>
          </p>

          <h4>
            New York
          </h4>

          <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1em' }}>
            The quest for the perfect sandwich
          </p>

          <p>
            New York City is a cultural hub for residents and travellers alike, a fact that’s most evident in its vast array of food. In a city full of extravagance, DAYMADE is focusing on the simple things. In this case, the sandwich. Because let’s be honest, there’s almost no better food that embodies the heritage of so many cultures fused into one bite. Eat your way through the history and evolution of the sarnie on a guided tour with a sandwich aficionado. An Airbnb sandwich tour through the city proves there’s no one way to sandwich.
          </p>

          <p>
            <a href='https://www.airbnb.com/experiences/438902?source=p2' style={{ fontWeight: 'bold', margin: 0 }}>
              AirBnB Experience →
            </a>
          </p>

          <h4>
            Kenya
          </h4>

          <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1em' }}>
            Farm your own coffee beans
          </p>

          <p>
            Not to be outdone by its caffeine-famous neighbour Ethiopia, Kenya is home to some of the best coffees in the world. A local coffee and tea farm tour in Kenya takes you on the journey of the coffee bean before it reaches your cup. These aroma-rich AirBnb tours will take you through the entire process: from coffee bean picking, all the way to roasting and grinding – and finally brewing and tasting a cup of coffee (most likely from the beans you roasted). What better way to get a taste for your morning cuppa than experiencing a local Kenyan coffee farm tour while on holiday?
          </p>

          <p>
            <a href='https://www.airbnb.com/experiences/236449?source=p2' style={{ fontWeight: 'bold', margin: 0 }}>
              AirBnB Experience →
            </a>
          </p>

          <h4>
            Bali
          </h4>

          <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1em' }}>
            High vibe ingredients only
          </p>

          <p>
            While Bali is famous for its family-run “Warung” restaurants dishing up morsels of affordable local cuisine, the green utopia has become a paradise destination for vegan food as well. You know that expensive moringa powder in your local health food store? Fresh moringa plants grow wild on the side of the road and are one of Bali’s countless local culinary secrets. Many of the spices and herbs that go into creating Balinese dishes are not only for taste, but also medicinal qualities. A cooking class will teach you how to identify these plants, combine spices and use locally grown ingredients to create simple dishes that you can recreate at home – and relive the Bali dream anytime you want. Whether you prefer raw vegan cakes or local Urap Urap, a cooking class with an Airbnb host in Bali is guaranteed to be an unforgettable experience. By the way, the highlight of any cooking class in Bali is the island’s traditional open kitchen design!
          </p>

          <p>
            <a href='https://www.airbnb.com/experiences/280331?source=p2' style={{ fontWeight: 'bold', margin: 0 }}>
              AirBnB Experience →
            </a>
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt='coffee beans being mixed in large sorting/mixing container'
            className='singleBlogImage'
          />

          <h4>
            Every meal tells a story
          </h4>

          <p>
            Discovering the yummiest local eats is often the preferred way of travel for food-lovers the world over. There’s always a new dish or flavour to discover when navigating through a country’s unique foodscape, for both newbies and seasoned travellers alike. Next time you take a jaunt around the globe, an Airbnb culinary experience will show you how to cook and eat like a local – long after you’ve boarded your return flight home.
          </p>

          <Message>
            Did you know that you can win a trip to all these destinations with DAYMADE? Get a ticket into the {''}
            <Link href='/'>
              <a style={{ margin: 0, fontWeight: 'bold' }}>draw</a>
            </Link>
            {''} and you may be able to taste these local cuisines sooner than you'd thought!
          </Message>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default AirBnbArticle;
